import apiClient, { ApiResponse } from './client';
import type {
  CalibrationStatus,
  CarryOverAnchor,
  ProductClass,
  SizeTier,
} from '@designdeck/shared';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

export interface ProductCategory {
  id: string;
  externalId?: number;
  name: string;
  slug: string;
  isActive: boolean;
  sortOrder: number;
  _count?: {
    products: number;
  };
}

export interface Manufacturer {
  id: string;
  externalId?: number;
  name: string;
  slug: string;
  website?: string;
}

export interface ProductSize {
  id: string;
  code: string;
  name: string;
  sortOrder: number;
}

export interface DecorationMethod {
  id: string;
  code: string;
  name: string;
  description?: string;
  minDpi?: number;
  maxColors?: number;
  minQuantity?: number;
  sortOrder: number;
}

export interface ProductColor {
  id: string;
  externalId?: number;
  name: string;
  hexCode: string;
  isColored: boolean;
  isHeathered: boolean;
  sortOrder: number;
  /** Model wearing this colour, for the guided catalogue. Storage key or URL. */
}

/**
 * A print area is physical: its size in millimetres. Where it sits on a
 * mockup is a property of each template (`templateRect`, R7); the area
 * carries no pixels.
 */
export interface PrintArea {
  id: string;
  rotation: number;
  maxWidthMm?: number | null;
  maxHeightMm?: number | null;
  offsetXCenterMm?: number;
  offsetYCollarMm?: number;
  /** Size tier derived by the catalogue sync from maxWidthMm */
  sizeTier?: SizeTier;
  /**
   * Admin marker of a wrap-around strip (RUNDUM). A KLEIN value still in
   * the catalogue reads as carry-over percent 100 (retire migration pending).
   */
  sizeTierOverride?: SizeTier | null;
  /** Area exception to the product's carry-over percent; null inherits */
  carryOverPercent?: number | null;
  /** Area exception to the product's vertical anchor; null inherits */
  carryOverAnchor?: CarryOverAnchor | null;
  /** Area exception to the product's top offset in mm; null inherits */
  carryOverTopOffsetMm?: number | null;
  supportedRatios?: { ratio: string; isDefault: boolean }[];
}

export interface ProductPosition {
  id: string;
  externalId?: number;
  name: string;
  slug: string;
  isDefault: boolean;
  sortOrder: number;
  /**
   * Direct Sale surcharge for decorating this position.
   * null means unpriced — the position is not offered in the Direct Sale
   * flow. 0 is a real price (free decoration). The Seller flow ignores it.
   */
  printSurcharge?: string | null;
  printArea?: PrintArea;
  /** The default method; how the position is normally decorated. */
  decorationMethods?: DecorationMethod | null;
  /**
   * Every method the position offers, default first. A position can be
   * printed by default and embroidered on request; the catalogue's run
   * filter and the embroidery method id on save both need the full list.
   */
  availableDecorationMethods?: AvailableDecorationMethod[];
}

export interface AvailableDecorationMethod {
  id: string;
  code: string;
  /** Name in the request language. */
  name?: string;
  /** False for a method an admin switched off; not sellable. */
  isActive?: boolean;
  /**
   * Direct Sale catalogue only: what the position costs with this method,
   * as the quote charges it; null when Direct Sale cannot sell the method
   * there. Absent from every other endpoint.
   */
  directSalePrice?: string | null;
  /** The method's ceiling — the one the server's publish rule applies. */
  maxColors?: number | null;
  minDpi?: number | null;
}

export interface ProductVariant {
  id: string;
  productId: string;
  productColorId?: string;
  productSizeId?: string;
  sku?: string;
  externalSku?: string;
  brandSku?: string;
  purchasePrice?: number;
  suggestedPrice?: number;
  weightGram?: number;
  customSizeLabel?: string;
  widthMm?: number;
  heightMm?: number;
  isAvailable: boolean;
  /** Retired at the supplier; such a variant cannot be sold. */
  isDiscontinued?: boolean;
  enforceDtf: boolean;
  productColor?: ProductColor;
  productSize?: ProductSize;
}

// ═══════════════════════════════════════════════════════════════════
// MOCKUP LAYER TYPES
// ═══════════════════════════════════════════════════════════════════

/**
 * Blend modes supported for mockup layer compositing
 * Maps to Canvas2D globalCompositeOperation
 */
export type MockupBlendMode =
  | 'NORMAL'
  | 'MULTIPLY'
  | 'SCREEN'
  | 'OVERLAY'
  | 'SOFT_LIGHT'
  | 'HARD_LIGHT';

/**
 * Configuration for an optional mockup layer
 */
export interface MockupLayerConfig {
  url: string;
  blendMode: MockupBlendMode;
  opacity: number;
}

// ═══════════════════════════════════════════════════════════════════
// MOCKUP TEMPLATE
// ═══════════════════════════════════════════════════════════════════

/**
 * Product Mockup Template with layered compositing support
 *
 * Layer Stack (bottom to top, Shirtigo Mockup Factory model):
 * 0. Background Color (ProductColor.hexCode)
 * 1. Orderable stack per `layerOrder`, default heathered → design → base → texture:
 *    - Heathered Overlay - OPTIONAL, only when ProductColor.isHeathered
 *    - Design Canvas (from Fabric.js) - positioned by offset/width/height
 *    - Base Image (imageUrl) - REQUIRED, a shading map composited ABOVE the
 *      design so folds darken the artwork (below it for embroidery)
 *    - Texture/Structure Overlay - OPTIONAL
 * 2. Shadow Overlay - OPTIONAL, pinned above the ordered stack (frames etc.)
 * 3. Cutout Mask (cutoutMaskUrl) - OPTIONAL, defines product silhouette
 */
export interface ProductMockupTemplate {
  id: string;
  name: string;
  positionKey: string;
  mockupType: string;

  // Base Image (REQUIRED): the shading map of the garment
  imageUrl: string;
  /** Opacity of the shading map (Shirtigo "Deckkraft Schatten-Layer") */
  baseOpacity?: number;
  /** Opacity of the design layer */
  designOpacity?: number;

  // Native mockup image dimensions (the canvas coordinate system)
  mockupWidthPx: number;
  mockupHeightPx: number;

  // Layer 3: Heathered/Meliert Overlay (OPTIONAL)
  heatheredOverlayUrl?: string;
  heatheredBlendMode?: MockupBlendMode;
  heatheredOpacity?: number;

  // Layer 4: Texture/Structure Overlay (OPTIONAL)
  textureOverlayUrl?: string;
  textureBlendMode?: MockupBlendMode;
  textureOpacity?: number;

  // Layer 5: Shadow Overlay (OPTIONAL) - always above the ordered stack,
  // below the cutout mask
  shadowOverlayUrl?: string | null;
  shadowBlendMode?: MockupBlendMode;
  shadowOpacity?: number;

  // Layer 6: Cutout Mask (OPTIONAL)
  cutoutMaskUrl?: string;
  /** Card-sized (480 px) variants for product cards; absent until rendered. */
  cardImageUrl?: string;
  cardCutoutMaskUrl?: string;

  // Overlay (OPTIONAL) - above the whole stack AND above the cutout mask,
  // never clipped: frames, clips, hangers, case edges
  topOverlayUrl?: string | null;
  /** Drawstrings and the like, drawn in the product colour above the design. */
  toplayerMaskUrl?: string | null;
  /** High-resolution base image for the server renderer and the editor canvas (e.g. 2000 px, same aspect ratio) */
  renderingImageUrl?: string | null;
  /** High-resolution cutout mask matching renderingImageUrl */
  renderingCutoutMaskUrl?: string | null;

  thumbnailUrl?: string;

  // Image size of the template
  widthPx: number;
  heightPx: number;

  // Calibration (R4): with the print area's mm the one formula
  // `templateRect` gives the print area rectangle on this template
  /** Pixels per mm at mockupWidthPx; null while uncalibrated */
  pxPerMm?: number | null;
  /** Centre of the print area relative to the image centre, in pixels */
  offsetXCenterPx?: number;
  /** Top edge of the print area, in pixels */
  offsetYTopPx?: number;
  /** Seller answers carry CALIBRATED templates only (R12) */
  calibrationStatus?: CalibrationStatus | null;

  isDefault?: boolean;
  isCreatorEditable?: boolean;

  /** Render order of the layer stack, bottom → top */
  layerOrder?: string[];
}

/**
 * Helper to get heathered layer config if available
 */
export function getHeatheredLayer(
  template: ProductMockupTemplate
): MockupLayerConfig | null {
  if (!template.heatheredOverlayUrl) return null;
  return {
    url: template.heatheredOverlayUrl,
    blendMode: template.heatheredBlendMode || 'OVERLAY',
    opacity: template.heatheredOpacity ?? 0.5,
  };
}

/**
 * Helper to get texture layer config if available
 */
export function getTextureLayer(
  template: ProductMockupTemplate
): MockupLayerConfig | null {
  if (!template.textureOverlayUrl) return null;
  return {
    url: template.textureOverlayUrl,
    blendMode: template.textureBlendMode || 'MULTIPLY',
    opacity: template.textureOpacity ?? 0.4,
  };
}

export interface Product {
  id: string;
  externalId?: number;
  artNumber?: string;
  name: string;
  slug: string;
  description?: string;
  descriptionEn?: string;
  /** Catalogue one-liner, e.g. "Regular Fit (S-3XL)". German only. */
  oneLineDescription?: string;
  /** Catalogue summary of a few words. German only. */
  shortDescription?: string;
  material?: string;
  hsCode?: string | null;
  hasColors: boolean;
  hasSizes: boolean;
  mockupImageUrl?: string;
  thumbnailImageUrl?: string;
  /** Model shot without a colour, fallback for colours that have none. */
  isOrganic?: boolean;
  recommendedSalesPrice?: number;
  /**
   * Blank reference price of the product for the seller's tenant (the most
   * frequent blank purchase price over the sellable variants); null when a
   * cost is not maintained. The supplier price itself is not exposed.
   */
  blankCost?: number | null;
  /**
   * What one printed side costs the seller at the least, net: the cheapest
   * blank plus the cheapest method on the front, with the channel markup;
   * null when either is unknown. The catalogue card shows this.
   */
  startingCost?: number | null;
  isActive: boolean;
  sortOrder: number;
  /** Shirtee shipping category the class was derived from; null when none. */
  shippingCategory?: number | null;
  /**
   * Carry-over class (TEXTILE, OBJECT) from the shipping category; null
   * means unknown and the class-change rules stay off for this product.
   */
  productClass?: ProductClass | null;
  /**
   * Carry-over percent (1-100): a design from another product is fitted
   * into a box of this share of each print area; null means default rules.
   */
  carryOverPercent?: number | null;
  /** Vertical anchor of that box; null means the class default. */
  carryOverAnchor?: CarryOverAnchor | null;
  /** Top offset of the box in mm for anchor TOP; null means the default. */
  carryOverTopOffsetMm?: number | null;
  category?: ProductCategory;
  manufacturer?: Manufacturer;
  colors?: ProductColor[];
  positions?: ProductPosition[];
  variants?: ProductVariant[];
  dimensions?: {
    productSizeId: string;
    widthCm: number;
    heightCm: number;
    lengthCm?: number;
  }[];
  mockupTemplates?: ProductMockupTemplate[];
}

export interface CustomerProductPricing {
  productId: string;
  variantPrices: {
    productVariantId: string;
    purchasePrice: number;
    suggestedPrice?: number;
    productVariant?: ProductVariant;
  }[];
}

// ═══════════════════════════════════════════════════════════════════
// QUERY TYPES
// ═══════════════════════════════════════════════════════════════════

export interface ProductListQuery {
  categoryId?: string;
  search?: string;
  isActive?: boolean;
}

// ═══════════════════════════════════════════════════════════════════
// API FUNCTIONS
// ═══════════════════════════════════════════════════════════════════

/**
 * Get all products
 */
export async function getProducts(
  query?: ProductListQuery
): Promise<Product[]> {
  const params = new URLSearchParams();
  if (query?.categoryId) params.append('categoryId', query.categoryId);
  if (query?.search) params.append('search', query.search);
  if (query?.isActive !== undefined)
    params.append('isActive', query.isActive.toString());

  const response = await apiClient.get<ApiResponse<Product[]>>(
    `/products?${params.toString()}`
  );
  return response.data.data;
}

/**
 * Get a single product by ID
 */
export async function getProduct(id: string): Promise<Product> {
  const response = await apiClient.get<ApiResponse<Product>>(`/products/${id}`);
  return response.data.data;
}

/**
 * Get a product by slug
 */
export async function getProductBySlug(slug: string): Promise<Product> {
  const response = await apiClient.get<ApiResponse<Product>>(
    `/products/by-slug/${slug}`
  );
  return response.data.data;
}

/**
 * Get product variants
 */
export async function getProductVariants(
  productId: string
): Promise<ProductVariant[]> {
  const response = await apiClient.get<ApiResponse<ProductVariant[]>>(
    `/products/${productId}/variants`
  );
  return response.data.data;
}

/**
 * Get all product categories
 */
export async function getCategories(): Promise<ProductCategory[]> {
  const response = await apiClient.get<ApiResponse<ProductCategory[]>>(
    '/products/categories'
  );
  return response.data.data;
}

/**
 * Get all product sizes
 */
export async function getSizes(): Promise<ProductSize[]> {
  const response =
    await apiClient.get<ApiResponse<ProductSize[]>>('/products/sizes');
  return response.data.data;
}

/**
 * Get all manufacturers
 */
export async function getManufacturers(): Promise<Manufacturer[]> {
  const response = await apiClient.get<ApiResponse<Manufacturer[]>>(
    '/products/manufacturers'
  );
  return response.data.data;
}

/**
 * Get all decoration methods
 */
export async function getDecorationMethods(): Promise<DecorationMethod[]> {
  const response = await apiClient.get<ApiResponse<DecorationMethod[]>>(
    '/products/decoration-methods'
  );
  return response.data.data;
}

/**
 * Get decoration method by code
 */
export async function getDecorationMethodByCode(
  code: string
): Promise<DecorationMethod> {
  const response = await apiClient.get<ApiResponse<DecorationMethod>>(
    `/products/decoration-methods/${code}`
  );
  return response.data.data;
}

/**
 * Get customer-specific pricing for a product
 */
export async function getCustomerPricing(
  productId: string
): Promise<CustomerProductPricing> {
  const response = await apiClient.get<ApiResponse<CustomerProductPricing>>(
    `/products/${productId}/pricing`
  );
  return response.data.data;
}
